import type { Permission } from "store/permissionsStore";

import type { PermissionCategory, PermissionItem } from "./types";

/**
 * Serialize conditions to create a unique, stable string representation
 * Recursively sorts keys at all levels for consistent output
 */
const serializeConditions = (conditions: any): string => {
    if (!conditions || Object.keys(conditions).length === 0) {
        return "";
    }

    // Recursively sort all keys
    const sortKeys = (obj: any): any => {
        if (Array.isArray(obj)) {
            return obj.map(sortKeys);
        }
        if (obj !== null && typeof obj === "object") {
            return Object.keys(obj)
                .sort()
                .reduce((result: any, key) => {
                    result[key] = sortKeys(obj[key]);
                    return result;
                }, {});
        }
        return obj;
    };

    return JSON.stringify(sortKeys(conditions));
};

/**
 * Generate a unique key for a permission including its conditions
 * Matches the format used in permissionsHelpers.ts: subject_action
 */
const generatePermissionKey = (action: string, subject: string, conditions?: any): string => {
    const baseKey = `${subject}_${action}`;
    const conditionsStr = serializeConditions(conditions);
    return conditionsStr ? `${baseKey}::${conditionsStr}` : baseKey;
};

/**
 * Parse a permission key back into its components
 * Expects format: subject_action (matching generatePermissionId in permissionsHelpers.ts)
 */
const parsePermissionKey = (key: string): { action: string; subject: string; conditions?: any } | null => {
    const parts = key.split("::");
    if (parts.length === 0) return null;

    const [subjectAction, conditionsStr] = parts;
    const subjectActionParts = subjectAction.split("_");

    if (subjectActionParts.length < 2) return null;

    const subject = subjectActionParts.slice(0, -1).join("_");
    const action = subjectActionParts[subjectActionParts.length - 1];

    if (conditionsStr) {
        try {
            const conditions = JSON.parse(conditionsStr);
            return { action, subject, conditions };
        } catch {
            return null;
        }
    }

    return { action, subject };
};

/**
 * Collect all permission items from categories (flattened)
 */
const collectAllPermissionItems = (categories: PermissionCategory[]): PermissionItem[] => {
    const allItems: PermissionItem[] = [];

    const collectItems = (items: PermissionItem[]) => {
        items.forEach((item) => {
            allItems.push(item);
            if (item.children) {
                collectItems(item.children);
            }
        });
    };

    categories.forEach((category) => {
        category.sections.forEach((section) => {
            collectItems(section.permissions);
        });
    });

    return allItems;
};

/**
 * Convert CASL Permission array to UI state
 */
export const permissionsToUIState = (
    permissions: Permission[],
    categories: PermissionCategory[],
): { [key: string]: boolean | any[] } => {
    const uiState: { [key: string]: boolean | any[] } = {};

    // Ensure permissions is an array
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
        return uiState;
    }

    const allPermissionItems = collectAllPermissionItems(categories);

    // Match API permissions to UI items
    permissions.forEach((permission) => {
        // Normalize action and subject to arrays for consistent handling
        const actions = Array.isArray(permission.action) ? permission.action : [permission.action];
        const subjects = Array.isArray(permission.subject) ? permission.subject : [permission.subject];

        // Create separate UI state entries for each action/subject combination
        subjects.forEach((subject) => {
            actions.forEach((action) => {
                // First try to match with a predefined UI item (for items in permission categories)
                const matchingItem = allPermissionItems.find(
                    (item) =>
                        item.action === action &&
                        item.subject === subject &&
                        JSON.stringify(item.conditions || {}) === JSON.stringify(permission.conditions || {}),
                );

                if (matchingItem) {
                    // Use the predefined ID from the UI category
                    uiState[matchingItem.id] = true;
                } else if (permission.conditions && typeof permission.conditions === "object") {
                    // Check if this is a simple field operator condition (e.g., { id: { $in: [...] } })
                    const conditionFields = Object.keys(permission.conditions);
                    if (conditionFields.length === 1) {
                        const field = conditionFields[0];
                        const fieldCondition = (permission.conditions as any)[field];

                        if (fieldCondition && typeof fieldCondition === "object") {
                            const operators = Object.keys(fieldCondition);
                            if (operators.length === 1) {
                                // Single field with single operator - store as key with value
                                const operator = operators[0];
                                const value = fieldCondition[operator];
                                const key = `${subject}_${action}::${field}_${operator}`;
                                uiState[key] = value;
                            } else {
                                // Multiple operators on single field - serialize
                                const key = generatePermissionKey(action, subject, permission.conditions);
                                uiState[key] = true;
                            }
                        } else {
                            // Not an object condition - serialize
                            const key = generatePermissionKey(action, subject, permission.conditions);
                            uiState[key] = true;
                        }
                    } else {
                        // Multiple fields - serialize
                        const key = generatePermissionKey(action, subject, permission.conditions);
                        uiState[key] = true;
                    }
                } else {
                    // Simple permission without conditions
                    const key = `${subject}_${action}`;
                    uiState[key] = true;
                }
            });
        });
    });

    return uiState;
};

/**
 * Convert UI state to CASL Permission array
 */
export const uiStateToPermissions = (
    uiState: { [key: string]: boolean | any[] },
    categories: PermissionCategory[],
): Permission[] => {
    const permissions: Permission[] = [];
    const allPermissionItems = collectAllPermissionItems(categories);

    // Convert UI items to CASL permissions
    Object.entries(uiState).forEach(([key, value]) => {
        // Skip false values (unchecked permissions)
        if (value === false) return;

        // First check if this key matches a predefined permission item
        const permissionItem = allPermissionItems.find((item) => item.id === key);

        if (permissionItem) {
            // Use the predefined permission configuration
            const permission: Permission = {
                action: permissionItem.action,
                subject: permissionItem.subject,
            };

            if (permissionItem.conditions) {
                permission.conditions = permissionItem.conditions;
            }

            permissions.push(permission);
        } else if (Array.isArray(value) || typeof value !== "boolean") {
            // Array or non-boolean value indicates a condition with operator
            // Format: Subject_action::field_operator
            // Note: Empty arrays mean no access (not access to all)
            const parts = key.split("::");
            if (parts.length === 2) {
                const [subjectAction, fieldOperator] = parts;
                const subjectActionParts = subjectAction.split("_");
                const fieldOperatorParts = fieldOperator.split("_");

                if (subjectActionParts.length >= 2 && fieldOperatorParts.length >= 2) {
                    const subject = subjectActionParts.slice(0, -1).join("_");
                    const action = subjectActionParts[subjectActionParts.length - 1];
                    const field = fieldOperatorParts.slice(0, -1).join("_");
                    const operator = fieldOperatorParts[fieldOperatorParts.length - 1];

                    // Always create permission, even with empty array
                    // Empty array with $in means no matches = no access
                    permissions.push({
                        action,
                        subject,
                        conditions: { [field]: { [operator]: value } },
                    });
                }
            }
        } else if (value === true) {
            // Boolean true - could be simple permission or complex condition
            if (key.includes("::")) {
                // Has conditions serialized in key
                const parsed = parsePermissionKey(key);
                if (parsed) {
                    const permission: Permission = {
                        action: parsed.action,
                        subject: parsed.subject,
                    };

                    if (parsed.conditions) {
                        permission.conditions = parsed.conditions;
                    }

                    permissions.push(permission);
                }
            } else {
                // Simple permission: Subject_action
                const parts = key.split("_");
                if (parts.length >= 2) {
                    const subject = parts.slice(0, -1).join("_");
                    const action = parts[parts.length - 1];
                    permissions.push({ action, subject });
                }
            }
        }
    });

    return permissions;
};
